import { getPreferredPhone, useGetAllContacts } from "@api/contactService";
import React, { useEffect, useState } from "react";
import Box from '@mui/joy/Box';
import IconButton from '@mui/joy/IconButton';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import ObjectIndex from "@components/objects/ObjectIndex";
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import ObjectInlineEdit from "@components/objects/ObjectInlineEdit";
import { useCreatePublicImprovement, useDeletePublicImprovement, useGetAllPublicImprovements, useUpdatePublicImprovement } from "@api/publicImprovementService";
import { formatCurrency } from "@utils/numbers";
import { formatDate } from "@utils/dates";
import { useCreateMembeshipLevel, useDeleteMembeshipLevel, useGetAllMembeshipLevels, useUpdateMembeshipLevel } from "@api/membershipLevelService";
import { useCreateTraining, useDeleteTraining, useGetAllTrainings, useUpdateTraining } from "@api/trainingService";
import ObjectPreview from "@components/objects/ObjectPreview";

const columns = [
    {
        id: 'name',
        numeric: false,
        disablePadding: true,
        sortable: true,
        label: 'Name',
        type: 'text',
        placeholder: 'e.g. How to Bake Marsha\'s Truffles',
        config: {
            required: true,
        },
        cell: (row, {LinkComponent}) => {
            // const LinkComponent = view === 'deleted' ? Box : Link;
            return (<>
                <Typography sx={{display: 'inline-block'}} fontWeight={'bold'}>{row.name}</Typography>
            </>)
        }
      },
      {
        id: 'description',
        numeric: false,
        disablePadding: false,
        sortable: false,
        label: 'Description',
        type: 'text',
        placeholder: 'e.g. in depth training on what makes Marsha\'s truffles the best',
        cell: (row) => row.description,
      },
      {
        id: 'location',
        numeric: false,
        disablePadding: false,
        sortable: false,
        label: 'Location',
        type: 'text',
        placeholder: 'e.g. Truffles on Tenth',
        cell: (row) => row.location,
      },
      {
        id: 'training_date',
        numeric: false,
        disablePadding: false,
        sortable: true,
        label: 'Date',
        type: 'date',
        cell: (row) => formatDate(row.training_date),
      },
      {
        id: 'contacts',
        numeric: false,
        disablePadding: false,
        sortable: true,
        label: 'Attendees',
        type: 'relationship',
        endpoint: 'contacts',
        multiple: true,
        loadAll: true,
        cell: (row) => {
            if(!row.contacts.length) {
                return null;
            }

            return (<>
                {row.contacts.map((contact, contactIndex) => (<React.Fragment key={contactIndex}>
                    <ObjectPreview key={contact.id} to={`/contacts/${contact.id}`} object={contact}>{contact.name}</ObjectPreview>
                    {contactIndex < row.contacts.length - 1 && `, `}
                </React.Fragment>))}
            </>);
        },
      },
];

const listCell = (row, {LinkComponent}) => {
    return (<>
    {row.name !== row.company_name && (
        <Box sx={{mb: 1}}>
            <Typography sx={{display: 'inline-block'}} fontWeight={'bold'}>
                <LinkComponent to={`/contacts/${row.id}`} object={row}>
                    {row.name}
                </LinkComponent>
            </Typography>
            {row.company_name && (<Typography fontSize="sm">{row.company_name}</Typography>)}
        </Box>
    )}
    {row.name === row.company_name && (
        <Box sx={{mb: 1}}>
            <Typography>
                <LinkComponent to={`/contacts/${row.id}`} object={row}>
                    {row.company_name}
                </LinkComponent>
            </Typography>
        </Box>
        )}
    </>);
};

const listActions = (row, {view, LinkComponent, ViewAction}) => {
    if(view === 'deleted') {
        return null;
    }

    return (<>
        <Tooltip title="Email">
            <IconButton
                size="sm"
                color="neutral"
                variant="outlined"
                sx={{ bgcolor: 'background.surface', ":disabled": {opacity: 0.3} }}
                disabled={!row.email}
                component={'a'}
                href={`mailto:${row.email}`}
                >
                <EmailRoundedIcon />
            </IconButton>
        </Tooltip>
        <Tooltip title="Call">
            <IconButton
                size="sm"
                color="neutral"
                variant="outlined"
                sx={{ bgcolor: 'background.surface', ":disabled": {opacity: 0.3} }}
                disabled={!getPreferredPhone(row)}
                component={'a'}
                href={`tel:${getPreferredPhone(row)}`}
                >
                <LocalPhoneRoundedIcon />
            </IconButton>
        </Tooltip>
    </>);
};

export default function Trainings() {
    return (
        <ObjectInlineEdit
            query={useGetAllTrainings}
            createMutation={useCreateTraining}
            updateMutation={useUpdateTraining}
            deleteMutation={useDeleteTraining}

            type={'training'}
            endpoint={'trainings'}
            permissionType={'settings'}
            title={'Trainings'}
            color={'default'}
            columns={columns}
            listCell={listCell}
            listActions={listActions}
        />
    )
}
